import { useEffect } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { APIError, ApiErrors } from '@interxfi/api-client/api-error';
import { useIxmUserauthV1SessionServiceSetPassword } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-session-service-set-password';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { AppPasswordInputWithLens } from '../../components/form/app-password-input';
import { AuthLayout } from '../../features/auth/layout';
import { UserauthPasswordInputWithLens, userauthPasswordValidator } from '../../features/userauth/user/password/userauth-password-input';
import { useErrorHandler } from '../../util/error-handler';
import { DEFAULT_UNAUTHENTICATED_ROUTE } from '../../util/routing/defaults';
import { getPageHead } from '../../util/routing/meta';

const resetPasswordSearchSchema = z.object({
  id: z.string().optional(),
  secret: z.string().optional(),
});

export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: resetPasswordSearchSchema,
  head: () => getPageHead('Reset Password'),
  component: ResetPassword,
});

const resetPasswordFormSchema = z
  .object({
    password: z.string().min(1, { error: 'Password is required' }),
    confirmPassword: userauthPasswordValidator.nonempty({ error: 'Password confirmation is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: 'Passwords must match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>;

const defaultValues: ResetPasswordFormValues = {
  password: '',
  confirmPassword: '',
};

function errorMessageGetter(error: Error | undefined) {
  if (error && error instanceof APIError) {
    if (error.message === ApiErrors.ForgotPasswordTokenExpired) {
      return 'Your password reset token has expired. Please request another password reset.';
    }
  }

  return 'An error occurred while resetting your password';
}

function ResetPassword() {
  const addToast = useAddToast();
  const navigate = useNavigate();
  const { secret, id: tokenId } = Route.useSearch();
  const { mutateAsync, isPending, error } = useIxmUserauthV1SessionServiceSetPassword();
  useErrorHandler(error, errorMessageGetter);

  const [buttonState, buttonRef] = useButtonState(isPending, error);
  const form = useForm({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(resetPasswordFormSchema),
  });

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = form;

  const lens = useLens({ control });

  async function onSubmit(value: ResetPasswordFormValues) {
    if (secret && tokenId) {
      try {
        await mutateAsync({
          newPassword: value.password,
          method: {
            forgotPasswordToken: { id: tokenId, secret },
          },
        });

        addToast({ id: 'passwordResetSuccess', content: 'Password was successfully reset.', variant: 'success' });

        await navigate({ to: DEFAULT_UNAUTHENTICATED_ROUTE });
      } catch {}
    }
  }

  const hasToken = Boolean(secret && tokenId);

  useEffect(() => {
    if (!hasToken) {
      addToast({
        id: 'passwordResetTokenMissing',
        content:
          'We could not find your password reset token. Please verify that you followed the link in your email, or request another password reset.',
        variant: 'error',
      });
    }
  }, [addToast, hasToken]);

  if (!hasToken) {
    return <Navigate to="/forgot-password" />;
  }

  return (
    <FormProvider {...form}>
      <AuthLayout>
        <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
          Reset Password
        </Typography>

        <VerticalStack as="form" gap={4} onSubmit={handleSubmit(onSubmit)}>
          <UserauthPasswordInputWithLens autoComplete="new-password" label="New Password" lens={lens.focus('password')} required />
          <AppPasswordInputWithLens autoComplete="new-password" label="Confirm Password" lens={lens.focus('confirmPassword')} required />

          <Button disabled={!isValid} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
            Reset Password
          </Button>
        </VerticalStack>

        <VerticalStack inlineAlign="center">
          <TextLink LinkElement={Link} size="medium" to="/login" underline="hover">
            Return to Sign In
          </TextLink>
        </VerticalStack>
      </AuthLayout>
    </FormProvider>
  );
}
